import { css } from '@emotion/react'
import {
  declineContact,
  deleteContact,
  patchContact,
  postContactBind,
  remindContactInvite,
  usePendingTrustedContactInvitationCount,
} from 'api/goodtrust/contact'
import { DASHBOARD_URL } from 'api/goodtrust/user/dashboard'
import { Avatar } from 'components/avatar/Avatar'
import { Button } from 'components/button/Button'
import { ContactDeleteModalContent } from 'components/contact/contactDeleteModal/ContactDeleteModal'
import {
  ContactInvitedModal,
  ContactInvitedModalConfig,
} from 'components/contact/contactInvitedModal/ContactInvitedModal'
import { ContactAcceptModal } from 'components/contact/contactNotificationModal/ContactAcceptModal'
import { ContactNotificationModal } from 'components/contact/contactNotificationModal/ContactNotificationModal'
import { useActualContact } from 'components/contact/contactUtils'
import { Icon } from 'components/icon'
import { ConfirmModal } from 'components/modal/confirmModal/ConfirmModal'
import { useModal } from 'components/modal/Modal'
import { usePopperModal } from 'components/modal/PopperModal'
import { handleAndToastError, toastCaughtError, toastSuccess } from 'components/Toast'
import { Text } from 'components/Typography'
import { WizardGridCard } from 'components/wizard/wizardGridCard/WizardGridCard'
import { useTranslation } from 'next-i18next'
import Router from 'next/router'
import { useState } from 'react'
import { mutate } from 'swr'
import { fireEvent } from 'utils/analytics'
import { colors } from 'utils/colors'
import { MessageException } from 'utils/error'
import { encodeQuery, unwrapResponse } from 'utils/fetcher'
import { toLowerCase } from 'utils/general'
import { useLoadingCallback } from 'utils/hooks'
import { pxToRem } from 'utils/styled'
import { ApiType, GenericFunction } from 'utils/types'
import { SAvatarContainer, SStatusContainer } from './ContactCard.styled'

export const ContactCard = ({
  contact,
  onChange,
}: {
  contact?: ApiType['TrustedContactResponse']
  onChange?: GenericFunction
}) => {
  const { t } = useTranslation('trusted_contact')
  const { t: tCommon } = useTranslation()

  const { actualContact, showContactUser } = useActualContact(contact)
  const [isLimited, setIsLimited] = useState(false)
  const { mutate: notifCountMutate } = usePendingTrustedContactInvitationCount()

  const [acceptModal, showAcceptModal] = useModal(
    ({ close, isOpen }) =>
      isOpen ? (
        <ContactNotificationModal
          close={close}
          notificationContents={[<ContactAcceptModal key={'jej'} contact={contact} />]}
        />
      ) : null,
    {
      modalCss: css`
        max-width: ${pxToRem(480)}rem;
      `,
    }
  )

  const [accept, isAccepting] = useLoadingCallback(async () => {
    try {
      fireEvent('tc_accept_clicked')
      if (!contact?.uuid) throw Error()
      await postContactBind({ uuid: contact.uuid }).then(unwrapResponse)
      await showAcceptModal()
      await mutate(DASHBOARD_URL)
      await notifCountMutate()
      await onChange?.()
    } catch (err) {
      handleAndToastError(err)
    }
  })

  const [deleteDecline, isDeletingDeclining] = useLoadingCallback(async () => {
    try {
      if (!contact?.uuid) throw Error()
      if (actualContact?.user?.state === 'PENDING' && !showContactUser) {
        await declineContact(contact.uuid).then(unwrapResponse)
      } else {
        await deleteContact(contact.uuid, {
          place: 'modal',
          invite_pending: contact?.user?.state === 'PENDING' ? 'yes' : 'no',
        }).then(unwrapResponse)
      }
      toastSuccess(showContactUser ? 'invitation_cancelled' : 'request_declined')
      await onChange?.()
    } catch (err) {
      toastCaughtError(err, 'something_went_wrong')
    }
  })

  const [invitedModal, showInvitedModal] = useModal(
    ({ close, isOpen }) =>
      isOpen ? (
        <ContactInvitedModal
          close={close}
          contact={contact}
          isReinvited={contact?.user?.state === 'PENDING'}
          isLimited={isLimited}
        />
      ) : null,
    ContactInvitedModalConfig
  )

  const [reinvite, isReinviting] = useLoadingCallback(
    async (send_type: ApiType['TrustedContactReminderRequest']['send_type']) => {
      try {
        if (!contact?.uuid) {
          throw new MessageException('something_went_wrong')
        }
        const res = await remindContactInvite(contact.uuid, contact.user?.type, { send_type })
        if (!res.ok) {
          // User has recently reinvited this person
          if (res.errorCode === 'trusted_contact_resend_too_soon') {
            fireEvent('tc_reminder_limit_reached', {
              type: contact.user?.type,
              reminder_type: send_type,
            })
            setIsLimited(true)
          } else throw Error(res.error?.toString())
        } else {
          setIsLimited(false)
        }
        showInvitedModal()
        onChange?.()
      } catch (err) {
        handleAndToastError(err)
      }
    }
  )

  const promoteDemote = async (isPromote?: boolean) => {
    try {
      if (!contact?.uuid) {
        throw new MessageException('something_went_wrong')
      }
      await patchContact(
        { type: isPromote ? 'LEGACY_CONTACT' : 'STANDARD' },
        { uuid: contact.uuid }
      ).then(unwrapResponse)
      fireEvent(isPromote ? 'tc_promoted' : 'tc_demoted', { place: 'modal' })
      toastSuccess(
        isPromote ? 'promoted_to_legacy_contact' : 'demoted_to_trusted_contact',
        {},
        { who: actualContact?.first_name || actualContact?.email || undefined }
      )
      await onChange?.()
    } catch (err) {
      handleAndToastError(err)
    }
  }

  const [demoteModal, showDemoteModal] = useModal(({ close, isOpen }) =>
    isOpen ? (
      <ConfirmModal
        icon={<Icon name="ShieldCross" />}
        title={t('trusted_contact.contacts.demote_modal_title', {
          who: actualContact?.first_name || actualContact?.email,
        })}
        description={t('trusted_contact.contacts.demote_modal_desc', {
          who: actualContact?.first_name || actualContact?.email,
        })}
        danger
        close={close}
        onConfirm={async () => await promoteDemote(false)}
        confirmText={t('trusted_contact.contacts.demote_modal_confirm', {
          who: actualContact?.first_name || actualContact?.email,
        })}
      />
    ) : null
  )

  const [promoteModal, showPromoteModal] = useModal(({ close, isOpen }) =>
    isOpen ? (
      <ConfirmModal
        icon={<Icon name="ShieldCheck" />}
        title={t('trusted_contact.contacts.promote_modal_title', {
          who: actualContact?.first_name || actualContact?.email,
        })}
        description={t('trusted_contact.contacts.promote_modal_desc')}
        close={close}
        onConfirm={async () => await promoteDemote(true)}
        confirmText={t('trusted_contact.contacts.promote_modal_confirm')}
      />
    ) : null
  )

  const [declineModal, showDeclineModal] = useModal(({ close, isOpen }) =>
    isOpen ? (
      <ConfirmModal
        title={t('trusted_contact.contacts.decline_modal_title', {
          who: actualContact?.first_name || actualContact?.email,
        })}
        close={close}
        onConfirm={deleteDecline}
        confirmText={t('trusted_contact.contacts.decline_modal_confirm')}
      >
        <ContactDeleteModalContent contact={contact} />
      </ConfirmModal>
    ) : null
  )

  const {
    modal: menu,
    setReferenceElement,
    open: openMenu,
  } = usePopperModal(
    () => (
      <>
        {contact?.user?.state === 'ACTIVE' ? (
          <>
            {contact?.user?.type === 'STANDARD' ? (
              <Button
                type="button"
                variant="text"
                onClick={() => {
                  fireEvent('tc_promote_open')
                  void showPromoteModal()
                }}
                leftIcon={<Icon name="ShieldCheck" />}
              >
                {t('trusted_contact.contacts.promote_to_legacy_button')}
              </Button>
            ) : (
              <Button
                type="button"
                variant="text"
                tone="danger"
                onClick={() => {
                  fireEvent('tc_demote_open')
                  void showDemoteModal()
                }}
                leftIcon={<Icon name="ShieldCross" />}
              >
                {t('trusted_contact.contacts.demote_to_trusted_button')}
              </Button>
            )}
            <Button
              type="button"
              variant="text"
              onClick={() =>
                void Router.push(
                  encodeQuery(`/me/trusted-contact/${contact?.uuid}`, { tab: 'settings' })
                )
              }
              leftIcon={<Icon name="DocumentEdit" />}
            >
              {t('trusted_contact.contacts.edit_contact_button')}
            </Button>
          </>
        ) : (
          <>
            {showContactUser ? (
              <>
                <Button
                  type="button"
                  variant="text"
                  onClick={() => reinvite('EMAIL')}
                  leftIcon={<Icon name="Send" />}
                >
                  {t('trusted_contact.contacts.resend_invite_button')}
                </Button>
                <Button
                  type="button"
                  variant="text"
                  tone="danger"
                  onClick={deleteDecline}
                  leftIcon={<Icon name="CancelCircle" />}
                >
                  {t('trusted_contact.contacts.cancel_invite_button')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="text"
                  onClick={accept}
                  leftIcon={<Icon name="CheckCircle" />}
                >
                  {t('trusted_contact.contacts.accept_request_button')}
                </Button>
                <Button
                  type="button"
                  variant="text"
                  tone="danger"
                  onClick={() => {
                    fireEvent('tc_decline_open')
                    void showDeclineModal()
                  }}
                  leftIcon={<Icon name="CancelCircle" />}
                >
                  {t('trusted_contact.contacts.decline_request_button')}
                </Button>
              </>
            )}
          </>
        )}
      </>
    ),
    { placement: 'bottom-end' }
  )

  const onClick = () => {
    if (showContactUser) {
      Router.push(`/me/trusted-contact/${contact?.uuid}`)
    } else if (contact?.user?.state === 'ACTIVE') {
      Router.push(`/me/shared/${contact?.uuid}`)
    } else accept()
  }

  const isLoading = isReinviting || isAccepting || isDeletingDeclining || actualContact == null

  return (
    <>
      {acceptModal}
      {invitedModal}
      {promoteModal}
      {demoteModal}
      {declineModal}
      {menu}

      <WizardGridCard
        noLineClamp
        ref={setReferenceElement}
        onClick={onClick}
        isLoading={isLoading}
        entityType={
          actualContact?.relationship
            ? tCommon(`common.relationships.${toLowerCase(actualContact?.relationship)}`)
            : ''
        }
        title={actualContact?.first_name || actualContact?.email}
        subtitle={
          actualContact?.user?.state === 'ACTIVE'
            ? actualContact?.first_name
              ? actualContact?.email
              : null
            : showContactUser
            ? t('trusted_contact.contacts.invite_sent')
            : t('trusted_contact.contacts.request_received')
        }
        image={
          <SAvatarContainer>
            <Avatar owner={actualContact} />
            <SStatusContainer>
              {actualContact?.user?.state === 'ACTIVE' ? (
                <Icon name="Check" color={colors.green[200]} />
              ) : showContactUser ? (
                <Icon name="Warning" color={colors.red[500]} />
              ) : (
                <Icon name="TimeClockFilled" color={colors.blue[500]} />
              )}
            </SStatusContainer>
          </SAvatarContainer>
        }
        brief={
          actualContact?.user?.state === 'ACTIVE' ? (
            <Text>
              {actualContact?.sharedItemCount && actualContact.sharedItemCount > 1
                ? t('trusted_contact.contacts.items_shared_plural', {
                    amount: actualContact.sharedItemCount,
                  })
                : t('trusted_contact.contacts.items_shared_singular', {
                    amount: actualContact.sharedItemCount,
                  })}
            </Text>
          ) : (
            <>
              {showContactUser ? (
                <Button
                  renderAs="div"
                  type="button"
                  variant="text"
                  tone={actualContact?.user?.state === 'DECLINED' ? 'danger' : 'gray'}
                  onClick={(event) => {
                    event.stopPropagation()
                    event.preventDefault()
                    reinvite('EMAIL')
                    return false
                  }}
                >
                  {actualContact?.user?.state === 'DECLINED'
                    ? t('trusted_contact.contacts.invite_declined')
                    : t('trusted_contact.contacts.resend_invite')}
                </Button>
              ) : (
                <Button
                  renderAs="div"
                  type="button"
                  variant="text"
                  onClick={(event) => {
                    event.stopPropagation()
                    event.preventDefault()
                    accept()
                    return false
                  }}
                >
                  <Text variant="bodySmall" css={{ color: 'inherit' }}>
                    {t('trusted_contact.contacts.accept_request')}
                  </Text>
                </Button>
              )}
            </>
          )
        }
        openMenu={showContactUser || actualContact?.user?.state !== 'ACTIVE' ? openMenu : undefined}
        noImageHeight
      />
    </>
  )
}

ContactCard.displayName = 'ContactCard'
